import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Gauge, Info } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../components/Toast';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';

// 配置项描述
const FIELD_META = [
  { key: 'target_rate_pct',         label: '目标成功率',     unit: '%',  min: 1,   max: 100,  step: 1,
    desc: '成功组数 ÷ 提取条数，达到此值才算达标。当天放量按“当天”成功率实时判断；次日额度的加减按“最近 3 天累计”成功率判断。建议 15~30%。' },
  { key: 'starter_quota',           label: '每日初始配额',   unit: '条', min: 1,   max: 500,  step: 10,
    desc: '每人每日的基础提取量。达标后次日恢复到此值，超额奖励也以此为基础计算。' },
  { key: 'unlock_rate_pct',         label: '达标解锁比例',   unit: '%',  min: 1,   max: 200,  step: 5,
    desc: '用完当天初始配额后，成功率达标时第一批放量的条数（当天初始配额 × 此比例）。它不改变每日总量：只要一直达标，最终都能提到每日总上限。' },
  { key: 'incentive_quota',         label: '激励配额',       unit: '条', min: 0,   max: 200,  step: 5,
    desc: '用完当天初始配额仍未达标时，最后再给的提取条数。用完仍未达标则今日暂停；成功率回升到目标后自动恢复。' },
  { key: 'penalty_rate_pct',        label: '未达标次日缩减', unit: '%',  min: 0,   max: 80,   step: 5,
    desc: '当天用完初始配额，且最近 3 天累计成功率仍未达标，次日初始配额按此比例递减（可连续递减，不低于“惩罚下限”）。若当天本身是奖励额度，次日只回到基准，不递减。' },
  { key: 'min_quota_rate_pct',      label: '惩罚下限',       unit: '%',  min: 10,  max: 90,   step: 5,
    desc: '递减的下限，占基准配额的比例（例：基准 100、下限 50% → 最低 50 条）。达标后次日恢复基准。' },
  { key: 'reward_rate_threshold_x', label: '超额奖励阈值',   unit: '倍', min: 1.5, max: 5,    step: 0.5,
    desc: '当天用完初始配额，且最近 3 天累计成功率达到目标的 N 倍，次日触发奖励（例：目标 15%、2 倍 → 累计成功率 ≥30%）。' },
  { key: 'reward_rate_pct',         label: '超额奖励条数',   unit: '条', min: 0,   max: 500,  step: 5,
    desc: '触发奖励后，次日初始配额在基准上增加的条数（例：基准 100 + 50 → 150 条）。只对次日有效、不叠加，且不超过每日总上限。' },
  { key: 'daily_cap',               label: '每日总上限',     unit: '条', min: 1,   max: 9999, step: 10,
    desc: '每人每日提取总量的上限，任何情况下都不会超过（已豁免的用户除外）。' },
  { key: 'max_per_request',         label: '单次绝对上限',   unit: '条', min: 1,   max: 100,  step: 1,
    desc: '单次提取的硬上限（最大 100），任何情况都不会超过，包括已豁免的用户。' },
];

// 状态说明
const STATUS_DOCS = [
  { status: '初始配额',     color: 'var(--c-primary)',  desc: '今日提取量还没用完当天初始配额，可直接提取，不看成功率。' },
  { status: '达标解锁',     color: 'var(--c-success)',  desc: '初始配额已用完，且成功率 ≥ 目标：额外解锁一批；之后仍达标可继续提到每日总上限。' },
  { status: '激励配额',     color: 'var(--c-accent)',   desc: '初始配额已用完，但成功率 < 目标：再给一批激励条数作为最后机会。' },
  { status: '今日暂停',     color: 'var(--c-danger)',   desc: '激励也用完且成功率仍 < 目标：暂停提取；成功率回升到目标后自动恢复。' },
  { status: '今日额度已满', color: 'var(--c-danger)',   desc: '今日提取量已达每日总上限，今日不可再提。' },
  { status: '已豁免',       color: 'var(--c-primary)',  desc: '该用户不受配额限制，只受“单次绝对上限”约束。' },
];

// 0 是合法值（表示“关闭这项”）；只有空值/非数字才使用默认值（旧写法 `x || 默认值` 会把 0 改回默认值）
const numOr = (v, d) => { const n = parseFloat(v); return Number.isFinite(n) ? n : d; };

export default function RateControlPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // 前端表单值（百分比字段在提交时换算为小数）
  const [form, setForm] = useState({
    enabled: false,
    target_rate_pct: 15,
    starter_quota: 100,
    unlock_rate_pct: 30,
    incentive_quota: 30,
    penalty_rate_pct: 20,
    min_quota_rate_pct: 50,
    reward_rate_threshold_x: 2,
    reward_rate_pct: 10,
    daily_cap: 300,
    max_per_request: 100,
  });

  const fetchCfg = useCallback(async () => {
    try {
      const res = await api.get('/admin/settings/rate-control');
      const d = res.data;
      setCfg(d);
      setForm({
        enabled: !!d.enabled,
        target_rate_pct:         Math.round((d.target_rate         || 0.15) * 100),
        starter_quota:           d.starter_quota           ?? 100,
        unlock_rate_pct:         Math.round((d.unlock_rate         || 0.30) * 100),
        incentive_quota:         d.incentive_quota         ?? 30,
        penalty_rate_pct:        Math.round((d.penalty_rate        ?? 0.20) * 100),
        min_quota_rate_pct:      Math.round((d.min_quota_rate      || 0.50) * 100),
        reward_rate_threshold_x: d.reward_rate_threshold   ?? 2,
        reward_rate_pct:         d.reward_rate_quota ?? Math.round((d.reward_rate || 0.10) * (d.starter_quota || 100)),
        daily_cap:               d.daily_cap               ?? 300,
        max_per_request:         d.max_per_request         ?? 100,
      });
    } catch (err) { showToast('加载失败', 'error'); }
  }, [showToast]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled:               form.enabled,
        target_rate:           parseFloat(form.target_rate_pct) / 100,
        starter_quota:         parseInt(form.starter_quota) || 100,
        unlock_rate:           parseFloat(form.unlock_rate_pct) / 100,
        incentive_quota:       Math.floor(numOr(form.incentive_quota, 30)),
        penalty_rate:          parseFloat(form.penalty_rate_pct) / 100,
        min_quota_rate:        parseFloat(form.min_quota_rate_pct) / 100,
        reward_rate_threshold: parseFloat(form.reward_rate_threshold_x) || 2,
        reward_rate:           numOr(form.reward_rate_pct, 10) / (parseFloat(form.starter_quota) || 100),
        daily_cap:             Number(form.daily_cap),
        max_per_request:       Number(form.max_per_request),
      };
      await api.put('/admin/settings/rate-control', payload);
      showToast('配置已保存，即时生效', 'success');
      fetchCfg();
    } catch (err) {
      showToast(err.response?.data?.error || '保存失败', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div className="header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin')} style={{ padding: '6px 8px' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>转换率配额管控</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          <button className="btn btn-ghost btn-sm" onClick={() => setShowHelp(v => !v)}
            style={{ padding: '6px 8px' }}>
            <Info size={18} />
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px', paddingBottom: 80 }}>

        {/* 帮助说明 */}
        {showHelp && (
          <div className="card" style={{ background: 'rgba(0,122,255,0.04)', border: '1px solid rgba(0,122,255,0.15)', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>判定逻辑</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 1.7 }}>
              每次用户（含后台代提的目标用户）提取数据时，系统按以下顺序判断本次可提量：<br />
              <b>① 今日提取量已达每日总上限</b> &#8594; 拒绝（今日额度已满）<br />
              <b>② 还没用完当天初始配额</b> &#8594; 直接给，不看成功率（初始配额）<br />
              <b>③ 初始配额用完后</b>，看实时成功率（今日成功组数 ÷ 今日提取条数）：<br />
              &nbsp;&nbsp;≥ 目标 &#8594; 可继续提取（达标解锁），一直到每日总上限<br />
              &nbsp;&nbsp;&lt; 目标 &#8594; 给激励配额；用完仍未达标 &#8594; 今日暂停，成功率回升到目标后自动恢复<br />
              <b>④ 次日额度</b>：只评判“当天用完初始配额”的人，按<b>最近 3 天累计成功率</b>（累计成功组数 ÷ 累计提取条数）判断是否达标、是否奖励；当天放量仍只看当天成功率<br />
              <b>⑤ 已豁免的用户</b>不受以上限制，只受单次绝对上限约束）<br />
              <b>④ 计算实时成功率</b>（今日 OCR 成功组数 ÷ 今日提取条数）<br />
              &nbsp;&nbsp;≥ 目标 &#8594; 达标单次上限 &nbsp;&nbsp;&lt; 目标 &#8594; 未达标单次上限
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>状态说明</div>
              {STATUS_DOCS.map(d => (
                <div key={d.status} style={{ display: 'flex', gap: 8, marginBottom: 5, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: d.color, flexShrink: 0, minWidth: 80 }}>{d.status}</span>
                  <span style={{ color: 'var(--c-text-secondary)' }}>{d.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 总开关 */}
        <div className="card" style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Gauge size={16} color={form.enabled ? 'var(--c-success)' : 'var(--c-text-secondary)'} />
                转换率配额管控
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                {form.enabled ? '已开启 · 所有用户（含后台代提的目标用户）均受限制，已豁免的用户除外' : '已关闭 · 不按成功率限制提取（单次绝对上限仍然生效）'}
              </div>
            </div>
            {/* 开关 */}
            <button
              onClick={() => handleChange('enabled', !form.enabled)}
              style={{
                width: 51, height: 31, borderRadius: 16, border: 'none', cursor: 'pointer',
                background: form.enabled ? 'var(--c-success)' : 'var(--c-divider)',
                position: 'relative', flexShrink: 0, transition: 'background 0.2s',
              }}>
              <div style={{
                width: 25, height: 25, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: form.enabled ? 23 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
          </div>
        </div>

        {/* 配置项 */}
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text-secondary)', marginBottom: 12 }}>参数配置</div>
          {FIELD_META.map((meta, idx) => {
            // 关闭管控时，除“单次绝对上限”外都置灰不可改；单次绝对上限在关闭时同样生效，必须保持可编辑
            const locked = !form.enabled && meta.key !== 'max_per_request';
            return (
            <div key={meta.key} style={{
              paddingBottom: idx < FIELD_META.length - 1 ? 14 : 0,
              marginBottom: idx < FIELD_META.length - 1 ? 14 : 0,
              borderBottom: idx < FIELD_META.length - 1 ? '0.5px solid var(--c-border)' : 'none',
              opacity: locked ? 0.5 : 1,
              pointerEvents: locked ? 'none' : 'auto',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ fontSize: 14, fontWeight: 600 }}>{meta.label}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="number"
                    min={meta.min}
                    max={meta.max}
                    step={meta.step}
                    value={form[meta.key]}
                    onChange={e => handleChange(meta.key, e.target.value)}
                    style={{
                      width: 80, textAlign: 'right', fontSize: 16, fontWeight: 700,
                      border: '1px solid var(--c-border)', borderRadius: 8,
                      padding: '4px 8px', background: 'var(--c-bg)', color: 'var(--c-text)',
                    }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', minWidth: 20 }}>{meta.unit}</span>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', lineHeight: 1.5 }}>{meta.desc}</div>
              {meta.key === 'max_per_request' && !form.enabled && (
                <div style={{ fontSize: 12, color: 'var(--c-accent)', lineHeight: 1.5, marginTop: 4 }}>
                  配额管控已关闭，但此项仍然生效：用户单次最多只能提这么多条。
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* 当前配置预览 */}
        {form.enabled && (
          <div className="card" style={{ background: 'rgba(52,199,89,0.04)', border: '1px solid rgba(52,199,89,0.15)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>规则预览</div>
            {(() => {
              const sq  = numOr(form.starter_quota, 100);
              const cap = numOr(form.daily_cap, 200);
              const inc = numOr(form.incentive_quota, 0);
              const rw  = numOr(form.reward_rate_pct, 10);
              const tgt = numOr(form.target_rate_pct, 15);
              const x   = numOr(form.reward_rate_threshold_x, 2);
              const minQ = Math.floor(sq * numOr(form.min_quota_rate_pct, 50) / 100);
              const rewardBase = Math.min(sq + rw, cap);
              return (
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 2 }}>
                  <b>当天怎么提</b><br />
                  每日初始 <b>{sq} 条</b>：直接提，不看成功率<br />
                  用完初始后，按当时的成功率决定还能不能继续提：<br />
                  &nbsp;&nbsp;· 达标（当天成功率 ≥{tgt}%）→ 可以继续提，累计最多到每日总上限 <b>{cap} 条</b><br />
                  &nbsp;&nbsp;· 未达标 → 只再给 <b>{inc} 条</b>激励（累计 <b>{Math.min(sq + inc, cap)} 条</b>），用完仍未达标则<b>今日暂停</b>；成功率回升到 {tgt}% 后自动恢复<br />
                  <b>次日额度</b>（只评判“当天用完了初始配额”的人；按最近 3 天累计成功率判断，不足 3 天的新用户按已有天数算）<br />
                  &nbsp;&nbsp;· 累计成功率未达标 → 次日初始配额 ×(1-{numOr(form.penalty_rate_pct, 20)}%)，最低 <b>{minQ} 条</b>；若当天本身是奖励额度，则只回到基准<br />
                  &nbsp;&nbsp;· 累计成功率达标 → 次日恢复基准 <b>{sq} 条</b><br />
                  &nbsp;&nbsp;· 累计成功率 ≥ {Math.round(tgt * x)}%（目标×{x}倍）→ 次日初始配额 <b>{rewardBase} 条</b>（+{rw} 条，不超过每日总上限），仅次日有效；奖励日里未达标最多提 {Math.min(rewardBase + inc, cap)} 条，达标最多提 {cap} 条<br />
                  &nbsp;&nbsp;· 当天没用完初始配额 → 不评判，既不减也不奖<br />
                  <b>硬上限</b>：每日总上限 <b>{cap} 条</b>（任何情况不超过）；单次上限 <b>{numOr(form.max_per_request, 100)} 条</b>（只限制每次提交最多提多少，一天可以提交多次）
                </div>
              );
            })()}
          </div>
        )}

        {/* 保存按钮 */}
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || cfg === null}
          style={{ width: '100%', height: 50, fontSize: 16, borderRadius: 12, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Save size={18} /> {saving ? '保存中...' : '保存配置'}
        </button>
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
